import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, UserCheck, UserPlus, Search, Home, Users as UsersIcon, Landmark, ArrowLeft, Mail, Phone, Clock, X, Building2, Layers, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError, formatPrice, asItems } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const BADGES = {
  PROPOSED: "bg-amber-100 text-amber-900 border-amber-300",
  REJECTED: "bg-rose-100 text-rose-900 border-rose-300",
  APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-300",
  UNASSIGNED: "bg-slate-100 text-slate-900 border-slate-300",
};

function safeRequirementEntries(value) {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? Object.entries(parsed).filter(([, item]) => item !== null && item !== "")
      : [];
  } catch {
    return [];
  }
}

export default function AdminLeadAssignments() {
  const [items, setItems] = useState([]);
  const [offices, setOffices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [assignDialog, setAssignDialog] = useState({ open: false, lead: null });
  const [officeId, setOfficeId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailOfficeId, setDetailOfficeId] = useState("");
  const [detailAgentId, setDetailAgentId] = useState("");
  const [rejectDialog, setRejectDialog] = useState({ open: false, lead: null });
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (selectedLead) {
      const proposedAgent = agents.find(a => String(a.user_id || a.id) === String(selectedLead.proposed_agent_user_id));
      const oId = proposedAgent?.office_id || selectedLead.listing_office_id || "";
      setDetailOfficeId(oId);
      setDetailAgentId(selectedLead.proposed_agent_user_id || selectedLead.listing_agent_id || "");
    } else {
      setDetailOfficeId("");
      setDetailAgentId("");
    }
  }, [selectedLead, agents]);

  // Deep linking modal states
  const [detailProperty, setDetailProperty] = useState(null);
  const [detailBuyer, setDetailBuyer] = useState(null);
  const [detailAgent, setDetailAgent] = useState(null);

  const load = async (currentSearch = search, currentShowAll = showAll) => {
    setLoading(true);
    setError("");
    try {
      const [lRes, oRes, aRes] = await Promise.allSettled([
        api.get("/v1/admin/crm/leads/pending-assignments", {
          params: { search: currentSearch.trim() || undefined, all: currentShowAll ? "true" : undefined }
        }),
        api.get("/v1/admin/directory/offices", { params: { limit: 200 } }),
        api.get("/v1/admin/directory/agents", { params: { limit: 200 } }),
      ]);
      setItems(lRes.status === "fulfilled" ? asItems(lRes.value.data) : []);
      setOffices(oRes.status === "fulfilled" ? asItems(oRes.value.data) : []);
      setAgents(aRes.status === "fulfilled" ? asItems(aRes.value.data) : []);
    } catch (e) {
      setItems([]);
      setOffices([]);
      setAgents([]);
      setError(formatApiError(e));
    }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load("", showAll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search, showAll);
  };

  const approve = async (lead) => {
    setBusy(lead.lead_id);
    try {
      await api.post(`/v1/admin/crm/leads/${lead.lead_id}/approve-assignment`);
      toast.success("Assignment approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const reject = async () => {
    const lead = rejectDialog.lead;
    const reason = rejectReason.trim();
    if (!lead || !reason) return;
    setBusy(lead.lead_id);
    try {
      await api.post(`/v1/admin/crm/leads/${lead.lead_id}/reject-assignment`, { reason });
      toast.success("Assignment rejected");
      setRejectDialog({ open: false, lead: null });
      setRejectReason("");
      if (selectedLead?.lead_id === lead.lead_id) setSelectedLead(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const openAssign = (lead) => {
    setOfficeId(lead.listing_office_id || "");
    setAgentId(lead.listing_agent_id || "");
    setAssignDialog({ open: true, lead });
  };

  const submitAssign = async () => {
    if (!officeId) { toast.error("Select an office"); return; }
    if (!agentId) { toast.error("Pick an agent"); return; }
    setBusy(assignDialog.lead.lead_id);
    try {
      await api.post(`/v1/office/crm/leads/${assignDialog.lead.lead_id}/propose-assignment`, {
        office_id: officeId,
        agent_user_id: agentId,
      });
      toast.success("Lead assigned successfully!");
      setAssignDialog({ open: false, lead: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const submitDetailAssign = async () => {
    if (!detailOfficeId) { toast.error("Select an office"); return; }
    if (!detailAgentId) { toast.error("Pick an agent"); return; }
    setBusy(selectedLead.lead_id);
    try {
      await api.post(`/v1/office/crm/leads/${selectedLead.lead_id}/propose-assignment`, {
        office_id: detailOfficeId,
        agent_user_id: detailAgentId,
      });
      toast.success("Lead assigned successfully!");
      setSelectedLead(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const openPropertyDetail = async (propertyId) => {
    if (!propertyId) return;
    try {
      // Assignment review must also work for draft/rejected inventory, so use
      // the protected admin projection instead of the published-only route.
      const { data } = await api.get(`/v1/admin/property-explorer/${propertyId}`);
      setDetailProperty(data?.property || data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openBuyerDetail = (lead) => {
    setDetailBuyer({
      name: lead.buyer_name || "—",
      email: lead.buyer_email || "—",
      phone: lead.buyer_phone || "—",
      source: lead.source || "Unavailable",
      created_at: lead.created_at || null
    });
  };

  const openAgentDetail = (lead) => {
    const office = offices.find(o => (o.office_id || o.id) === lead.assigned_office_id || (o.office_id || o.id) === lead.proposed_office_id);
    setDetailAgent({
      name: lead.proposed_agent_name,
      email: lead.proposed_agent_email || "—",
      phone: lead.proposed_agent_phone || "—",
      office_name: office ? office.name : "Unassigned Office"
    });
  };

  const filteredAgents = agents.filter(a => {
    if (!officeId) return false;
    return String(a.office_id || a.raw?.office_id) === String(officeId);
  });

  return (
    <div className="space-y-6" data-testid="admin-lead-assignments-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-1.5">Operations · CRM</p>
        <h1 className="font-display text-3xl font-black tracking-tight text-primary uppercase"><span className="brand-gradient-text">Lead Assignment</span> Control</h1>
        <p className="text-xs text-muted-foreground">Approve agent assignments proposed by verifier offices, or assign unassigned leads directly.</p>
      </div>

      {/* Search and Filters Strip */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 items-center bg-card border border-border/80 p-4 rounded-xl shadow-sm">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search leads by buyer name, phone, email, or property..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-9 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0 self-start sm:self-center">
          <input
            type="checkbox"
            id="show-all-leads"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="w-4 h-4 rounded border-input bg-background text-primary"
          />
          <label htmlFor="show-all-leads" className="text-xs font-semibold text-foreground cursor-pointer select-none">
            Include Assigned Leads
          </label>
        </div>
        <Button type="submit" size="sm" className="font-bold shrink-0 w-full sm:w-auto">
          Search
        </Button>
      </form>

      {loading ? (
        <div className="grid gap-3" aria-label="Loading lead assignments">
          {[0, 1, 2].map((row) => (
            <Card key={row} className="p-5 space-y-3">
              <div className="flex justify-between gap-4"><Skeleton className="h-5 w-28" /><Skeleton className="h-8 w-24" /></div>
              <Skeleton className="h-5 w-2/3" /><Skeleton className="h-3 w-1/2" />
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive/30 bg-destructive/5 p-8 text-center" role="alert"><p className="text-sm font-bold text-foreground">Lead assignments could not be loaded.</p><p className="mt-1 text-xs text-muted-foreground">{error}</p><Button type="button" size="sm" variant="outline" className="mt-4 gap-2" onClick={() => load(search, showAll)}><RefreshCw className="h-4 w-4" aria-hidden="true"/>Retry</Button></Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-xs text-muted-foreground border-dashed border-2 bg-card" data-testid="no-pending-assignments">No pending or unassigned leads matching filter.</Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className={`${selectedLead ? "lg:col-span-7" : "lg:col-span-12"} space-y-3`} data-testid="assignments-list">
            {items.map((lead) => {
              const isProposed = lead.assignment_status === "PROPOSED";
              const badgeKey = isProposed ? "PROPOSED" : (lead.assignment_status || "UNASSIGNED");
              const isSelected = selectedLead?.lead_id === lead.lead_id;
              return (
                <Card
                  key={lead.lead_id}
                  role="group"
                  aria-label={`Lead assignment for ${lead.buyer_name || "buyer"}`}
                  className={`p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border transition shadow-sm cursor-pointer ${
                    isSelected ? "border-primary ring-1 ring-primary/30 bg-muted/10" : "border-border/80 hover:border-primary/30"
                  }`}
                  onClick={() => {
                    setSelectedLead(lead);
                    if (lead.property_id) {
                      openPropertyDetail(lead.property_id);
                    } else {
                      setDetailProperty(null);
                    }
                  }}
                  data-testid={`assignment-card-${lead.lead_id}`}
                >
                  <div className="space-y-2 min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={BADGES[badgeKey] || BADGES.UNASSIGNED}>{badgeKey}</Badge>
                      <span className="text-[10px] text-muted-foreground font-mono">Lead ID: {String(lead.lead_id).slice(-8).toUpperCase()}</span>
                    </div>
                    
                    {/* Property Link */}
                    {lead.property_id ? (
                      <Link
                        to={`/properties/${lead.property_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-bold truncate text-sm text-primary hover:text-primary/80 hover:underline flex items-center gap-1.5 w-fit"
                      >
                        <Home className="w-4 h-4 shrink-0 text-muted-foreground" />
                        Property: {lead.property_title || lead.property_code || lead.property_id}
                      </Link>
                    ) : lead.plot_demand_id ? (
                      <Link
                        to={`/dashboard/admin/plots-on-demand/workbench?select=${encodeURIComponent(lead.plot_demand_id)}`}
                        onClick={(event) => event.stopPropagation()}
                        className="font-bold truncate text-sm text-primary hover:underline flex items-center gap-1.5 w-fit"
                        title="Open this Plots on Demand request"
                      >
                        <Landmark className="w-4 h-4 shrink-0" />
                        Plots on Demand request
                      </Link>
                    ) : (
                      <div className="font-bold truncate text-sm text-muted-foreground flex items-center gap-1.5">
                        <Home className="w-4 h-4 shrink-0" />
                        General enquiry (No Listing)
                      </div>
                    )}

                    {/* Buyer Link */}
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center shrink-0">
                        {lead.buyer_avatar_url ? (
                          <img src={lead.buyer_avatar_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[9px] font-bold text-primary bg-primary/10 w-full h-full flex items-center justify-center">
                            {String(lead.buyer_name || "—").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span>
                        Buyer:{" "}
                        <Link 
                          to={`/dashboard/admin/users?select=${lead.buyer_user_id || ""}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-bold text-foreground hover:text-primary hover:underline"
                        >
                          {lead.buyer_name || "—"}
                        </Link>{" "}
                        · {lead.buyer_phone_masked || lead.buyer_phone || "—"}
                      </span>
                    </div>

                    {/* Agent Link */}
                    <div className="text-xs flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center shrink-0">
                        {lead.proposed_agent_avatar_url || lead.assigned_to_avatar_url ? (
                          <img src={lead.proposed_agent_avatar_url || lead.assigned_to_avatar_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[9px] font-bold text-primary bg-primary/10 w-full h-full flex items-center justify-center">
                            {String(lead.proposed_agent_name || "?").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      {lead.proposed_agent_name ? (
                        <span>
                          {lead.assignment_status === "APPROVED" 
                            ? "Assigned agent: " 
                            : lead.assignment_status === "REJECTED"
                            ? "Rejected agent: "
                            : "Proposed agent: "}
                          <Link 
                            to={`/dashboard/admin/users?select=${lead.proposed_agent_user_id || lead.assigned_agent_id || lead.assigned_to_user_id || ""}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-bold text-foreground hover:text-primary hover:underline"
                          >
                            {lead.proposed_agent_name}
                          </Link>
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic">No agent assigned</span>
                      )}
                    </div>
                    {lead.proposed_note && <div className="text-xs text-muted-foreground italic">"{lead.proposed_note}"</div>}
                  </div>
                  
                  <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={(event) => {
                      event.stopPropagation();
                      setSelectedLead(lead);
                      if (lead.property_id) openPropertyDetail(lead.property_id);
                      else setDetailProperty(null);
                    }} aria-pressed={isSelected} title="Open lead assignment details">
                      View details
                    </Button>
                    {isProposed ? (
                      <>
                        <Button size="sm" className="gap-2 font-bold" disabled={busy === lead.lead_id}
                          onClick={(e) => { e.stopPropagation(); approve(lead); }} data-testid={`btn-approve-${lead.lead_id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5"/> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="gap-2 font-bold" disabled={busy === lead.lead_id}
                          onClick={(e) => { e.stopPropagation(); setRejectReason(""); setRejectDialog({ open: true, lead }); }} data-testid={`btn-reject-${lead.lead_id}`}>
                          <XCircle className="w-3.5 h-3.5"/> Reject
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" className="gap-2 font-bold" disabled={busy === lead.lead_id}
                        onClick={(e) => { e.stopPropagation(); openAssign(lead); }} data-testid={`btn-assign-${lead.lead_id}`}>
                        <UserPlus className="w-3.5 h-3.5"/> 
                        {lead.assignment_status === "APPROVED" ? "Change Agent" : "Assign Agent"}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {selectedLead && (
            <div className="lg:col-span-5 bg-card border border-border rounded-xl p-5 shadow-sm space-y-5 sticky top-6 self-start max-h-[calc(100vh-200px)] overflow-y-auto" data-testid="lead-detail-pane">
              <div className="flex justify-between items-center border-b border-border pb-3">
                <div>
                  <h2 className="font-display font-bold text-sm tracking-wide uppercase text-primary">Lead Details</h2>
                  <span className="text-[10px] text-muted-foreground font-mono">ID: {String(selectedLead.lead_id).slice(-8).toUpperCase()}</span>
                </div>
                <Button variant="ghost" size="xs" onClick={() => setSelectedLead(null)} className="h-6 w-6 p-0 rounded-full hover:bg-muted" aria-label="Close lead details" title="Close lead details">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4 text-xs">
                {/* Status */}
                <div className="flex justify-between items-center bg-muted/30 p-2.5 rounded-lg border border-border/40">
                  <span className="text-muted-foreground font-semibold">Assignment Status</span>
                  <Badge variant="outline" className={BADGES[selectedLead.assignment_status || "UNASSIGNED"] || BADGES.UNASSIGNED}>
                    {selectedLead.assignment_status || "UNASSIGNED"}
                  </Badge>
                </div>

                {/* Buyer Profile */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-foreground uppercase tracking-wider text-[10px] text-primary flex items-center gap-1.5">
                    <UsersIcon className="w-3.5 h-3.5 text-muted-foreground" /> Buyer Profile
                  </h3>
                  <div className="bg-muted/10 p-3 rounded-lg border border-border/40 space-y-2">
                    <div className="flex items-center gap-3 mb-2 pb-2 border-b border-border/30">
                      <div className="w-10 h-10 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center shrink-0">
                        {selectedLead.buyer_avatar_url ? (
                          <img src={selectedLead.buyer_avatar_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-bold text-primary bg-primary/10 w-full h-full flex items-center justify-center">
                            {String(selectedLead.buyer_name || "—").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <Link 
                          to={`/dashboard/admin/users?select=${selectedLead.buyer_user_id || ""}`}
                          title="Click to view buyer profile page"
                          className="font-bold text-foreground hover:text-primary hover:underline text-sm"
                        >
                          {selectedLead.buyer_name || "—"}
                        </Link>
                        <p className="text-[10px] text-muted-foreground">Registered Buyer</p>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Phone:</span>
                      <a href={`tel:${selectedLead.buyer_phone}`} className="text-primary hover:underline font-semibold">
                        {selectedLead.buyer_phone || "—"}
                      </a>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <a href={`mailto:${selectedLead.buyer_email}`} className="text-primary hover:underline">
                        {selectedLead.buyer_email || "—"}
                      </a>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Source:</span>
                      <span className="text-foreground capitalize">{String(selectedLead.source || "website").toLowerCase()}</span>
                    </div>
                  </div>
                </div>

                {/* Listing or Requirements */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-foreground uppercase tracking-wider text-[10px] text-primary flex items-center gap-1.5">
                    <Home className="w-3.5 h-3.5 text-muted-foreground" /> Listing / Requirement
                  </h3>
                  <div className="bg-muted/10 p-3 rounded-lg border border-border/40 space-y-2">
                    {selectedLead.property_id ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Listing Title:</span>
                          <Link to={`/properties/${selectedLead.property_id}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold max-w-[180px] truncate block text-right">
                            {selectedLead.property_title || "View Listing"}
                          </Link>
                        </div>
                        {detailProperty && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Price:</span>
                              <span className="text-foreground font-bold font-mono">{detailProperty.price != null ? formatPrice(detailProperty.price) : "Price unavailable"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Locality:</span>
                              <span className="text-foreground">{detailProperty.locality || "—"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">City:</span>
                              <span className="text-foreground">{detailProperty.city || "—"}</span>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-bold text-foreground">Plots on Demand Request</p>
                          {selectedLead.plot_demand_id && (
                            <Link
                              to={`/dashboard/admin/plots-on-demand/workbench?select=${encodeURIComponent(selectedLead.plot_demand_id)}`}
                              className="text-xs font-bold text-primary hover:underline"
                              title="Open the full Plots on Demand request"
                            >
                              Open request
                            </Link>
                          )}
                        </div>
                        {selectedLead.raw && (
                          <div className="space-y-1 mt-1 border-t border-border/30 pt-1">
                            {safeRequirementEntries(selectedLead.raw).map(([k, v]) => (
                              <div key={k} className="flex justify-between text-[10px]">
                                <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}:</span>
                                <span className="text-foreground text-right max-w-[160px] truncate">{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Agent Assignment */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-foreground uppercase tracking-wider text-[10px] text-primary flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-muted-foreground" /> Agent Assignment
                  </h3>
                  <div className="bg-muted/10 p-3.5 rounded-lg border border-border/40 space-y-3">
                    {selectedLead.proposed_agent_name && (
                      <div className="flex items-center gap-3 mb-2 pb-2 border-b border-border/30">
                        <div className="w-10 h-10 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center shrink-0">
                          {selectedLead.proposed_agent_avatar_url || selectedLead.assigned_to_avatar_url ? (
                            <img src={selectedLead.proposed_agent_avatar_url || selectedLead.assigned_to_avatar_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-sm font-bold text-primary bg-primary/10 w-full h-full flex items-center justify-center">
                              {String(selectedLead.proposed_agent_name).charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <Link 
                            to={`/dashboard/admin/users?select=${selectedLead.proposed_agent_user_id || selectedLead.assigned_agent_id || selectedLead.assigned_to_user_id || ""}`}
                            title="Click to view agent profile page"
                            className="font-bold text-foreground hover:text-primary hover:underline text-sm"
                          >
                            {selectedLead.proposed_agent_name}
                          </Link>
                          <p className="text-[10px] text-muted-foreground">{selectedLead.proposed_agent_email || ""}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Office Selection */}
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold text-muted-foreground uppercase">Assign Office</Label>
                      <Select 
                        value={detailOfficeId} 
                        onValueChange={(val) => {
                          setDetailOfficeId(val);
                          setDetailAgentId("");
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs bg-background">
                          <SelectValue placeholder="Select Office" />
                        </SelectTrigger>
                        <SelectContent>
                          {offices.map((o) => (
                            <SelectItem key={o.office_id || o.id} value={o.office_id || o.id}>{o.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Agent Selection */}
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold text-muted-foreground uppercase">Assign Agent</Label>
                      <Select 
                        value={detailAgentId} 
                        onValueChange={setDetailAgentId}
                        disabled={!detailOfficeId}
                      >
                        <SelectTrigger className="h-8 text-xs bg-background">
                          <SelectValue placeholder={detailOfficeId ? "Select Agent" : "Select Office First"} />
                        </SelectTrigger>
                        <SelectContent>
                          {agents
                            .filter(a => String(a.office_id || a.raw?.office_id) === String(detailOfficeId))
                            .map((a) => (
                              <SelectItem key={a.user_id || a.id} value={a.user_id || a.id}>{a.name}</SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedLead.proposed_note && (
                      <div className="mt-2 p-2 bg-muted/40 rounded border-l-2 border-primary text-[10px] text-muted-foreground italic">
                        Proposal Note: "{selectedLead.proposed_note}"
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-3 border-t border-border mt-4 justify-end">
                {selectedLead.assignment_status === "PROPOSED" ? (
                  <>
                    <Button size="sm" variant="outline" className="font-bold border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => { setRejectReason(""); setRejectDialog({ open: true, lead: selectedLead }); }}>
                      Reject Proposal
                    </Button>
                    {detailAgentId === selectedLead.proposed_agent_user_id ? (
                      <Button size="sm" className="font-bold" onClick={() => approve(selectedLead)}>
                        Approve Proposal
                      </Button>
                    ) : (
                      <Button size="sm" className="font-bold" onClick={submitDetailAssign}>
                        Approve &amp; Assign
                      </Button>
                    )}
                  </>
                ) : (
                  <Button size="sm" className="font-bold" onClick={submitDetailAssign}>
                    {selectedLead.assignment_status === "APPROVED" ? "Re-assign Agent" : "Assign Agent"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Direct Assignment Hierarchical Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(v) => !v && setAssignDialog({ open: false, lead: null })}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight uppercase">Assign Agent to Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Office</Label>
              <Select value={officeId} onValueChange={(val) => { setOfficeId(val); setAgentId(""); }}>
                <SelectTrigger data-testid="select-assign-office"><SelectValue placeholder="Select an office"/></SelectTrigger>
                <SelectContent>
                  {offices.map((o) => (
                    <SelectItem key={o.office_id || o.id} value={o.office_id || o.id}>{o.name} · {o.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Agent</Label>
              <Select value={agentId} onValueChange={setAgentId} disabled={!officeId}>
                <SelectTrigger data-testid="select-assign-agent"><SelectValue placeholder={officeId ? "Pick an agent" : "Select an office first"}/></SelectTrigger>
                <SelectContent>
                  {filteredAgents.map((a) => (
                    <SelectItem key={a.user_id || a.id} value={a.user_id || a.id}>{a.name} · {a.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, lead: null })}>Cancel</Button>
            <Button onClick={submitAssign} disabled={!agentId || busy} className="font-bold" data-testid="btn-submit-assign">
              {busy ? "Assigning…" : "Assign Agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accessible confirmation keeps the required reason visible and reviewable. */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => {
        if (!open && !busy) {
          setRejectDialog({ open: false, lead: null });
          setRejectReason("");
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="reject-assignment-dialog">
          <DialogHeader>
            <DialogTitle>Reject this assignment proposal?</DialogTitle>
            <DialogDescription>
              Record a clear reason for the office and agent before rejecting the proposal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="admin-assignment-rejection-reason">Rejection reason *</Label>
            <Textarea
              id="admin-assignment-rejection-reason"
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="Explain why this assignment is not suitable"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={!!busy} onClick={() => {
              setRejectDialog({ open: false, lead: null });
              setRejectReason("");
            }}>Keep proposal</Button>
            <Button type="button" variant="destructive" disabled={!!busy || !rejectReason.trim()} onClick={reject}>
              {busy ? "Rejecting…" : "Reject proposal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
}
